import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDateParserFormatter, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateFRParserFormatter } from './../inspections-scheduled/ngb-date-fr-parser-formatter';
import { EstimatedActualService } from '../../services/estimated-actual/estimated-actual.service';
import { CommonService } from '../../services/common/common.service';
import { downloadCsv, matches, pageNumbers, toNumber, REPORT_DATE_RANGES } from '../../report-view';

interface EstimateJob {
  jobid: string;
  address: string;
  estimated_hours: any;
  actual_hours: any;
  perc_hours: any;
  estimated_materials: any;
  actual_materials: any;
  perc_mat: any;
}

interface EstimateGroup {
  key: string;
  name: string;
  jobs: EstimateJob[];
  totals: any;
}

/**
 * Estimated vs Actual (mockup reports/estimated-vs-actual). Same API (estimated_report), filters,
 * date presets and inspector rule as before; one collapsible card per inspector with the API's
 * totals, plus summary tiles added up from those totals. Also embedded on the old admin dashboard /
 * home widgets ([count]), where the page head is hidden as before.
 */
@Component({
  selector: 'app-estimated-actual',
  templateUrl: './estimated-actual.component.html',
  providers: [{ provide: NgbDateParserFormatter, useClass: NgbDateFRParserFormatter }, CommonService, EstimatedActualService],
  styleUrls: ['./estimated-actual.component.scss']
})
export class EstimatedActualComponent implements OnInit, OnChanges {
  @Input() count: number;
  readonly pageSizes = [5, 10, 25];
  readonly embedded: boolean;
  readonly dateRanges = REPORT_DATE_RANGES;

  report = this.emptyFilters(true);
  branches = [];
  inspectors = [];
  /** Branch, Inspector and Active filters: not for inspectors. */
  inspectorCheck = false;
  groups: EstimateGroup[] = [];
  loaded = false;
  errordetails = false;
  loading = false;
  search = '';
  pageSize = 5;
  page = 1;
  collapsed: { [key: string]: boolean } = {};
  today = new Date();

  constructor(private _router: Router, private calendar: NgbCalendar, private estimateService: EstimatedActualService,
    private commonService: CommonService) {
    this.embedded = _router.url.split('?')[0] !== '/accounting-report-estimated-vs-actual';
  }

  ngOnInit() {
    this.commonService.getBranch().subscribe(data => this.branches = data);
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    this.inspectorCheck = !!user && user.user_level != 'inspector';
    if (this.inspectorCheck) {
      this.commonService.getInspectors().subscribe(data => this.inspectors = data);
    }
  }

  ngOnChanges() {
    // Date range picked on the dashboard widget.
    if (this.count) {
      this.report.dateRange = this.count;
      this.dateRangeSubmit(this.count);
      this.estimateReport();
    }
  }

  // Active inspectors starts ticked; Clear unticks it, as before.
  private emptyFilters(active: boolean) {
    return { branch: '', inspector: '', active, start: null, end: null, dateRange: 0 as any };
  }

  estimateReport() {
    if (!this.report.start || !this.report.end) {
      this.errordetails = true;
      return false;
    }
    this.errordetails = false;
    this.loading = true;
    this.estimateService.getEstimated(this.report).subscribe(
      data => {
        this.loading = false;
        this.loaded = true;
        this.today = new Date();
        this.groups = this.toGroups(data);
        this.collapsed = {};
        this.page = 1;
      },
      () => this.loading = false
    );
  }

  private toGroups(data: any): EstimateGroup[] {
    if (!data || typeof data !== 'object') {
      return [];
    }
    return Object.keys(data).sort((a, b) => +a - +b).map(key => {
      const value = data[key] || {};
      const jobs = value.jobs && typeof value.jobs === 'object'
        ? Object.keys(value.jobs).map(k => value.jobs[k]) : [];
      return {
        key,
        name: (value.user_info && value.user_info.name) || 'No inspector',
        jobs,
        totals: value.user_tot_info || {},
      };
    });
  }

  clear() {
    this.report = this.emptyFilters(false);
    this.search = '';
    this.errordetails = false;
  }

  dateRangeSubmit(value) {
    this.report.end = this.calendar.getToday();
    this.report.start = this.commonService.dateRangeSubmit(value);
  }

  // ---- Cells ----

  num(value: any): number {
    const n = toNumber(value);
    return n === null ? 0 : n;
  }

  /** Hours / materials used: a pill, green up to 100%, red over it; nothing used yet is grey. */
  usage(perc: any, actual: any, idle: string): { text: string, tone: string } {
    const p = this.num(perc);
    if (!p) {
      return { text: this.num(actual) ? 'N/A' : idle, tone: 'neutral' };
    }
    return { text: p.toFixed(2) + '%', tone: p > 100 ? 'over' : 'good' };
  }

  /** Estimated minus actual materials: positive means under estimate. */
  materialsSaved(totals: any): number {
    return this.num(totals.estimated_mat_total) - this.num(totals.actual_mat_total);
  }

  // ---- Summary tiles (whole result, not just the visible page) ----

  get jobCount(): number {
    return this.groups.reduce((n, g) => n + g.jobs.length, 0);
  }

  sumTotals(field: string): number {
    return this.groups.reduce((n, g) => n + this.num(g.totals[field]), 0);
  }

  get totalMaterialsSaved(): number {
    return this.groups.reduce((n, g) => n + this.materialsSaved(g.totals), 0);
  }

  // ---- Cards: search, paging, collapse ----

  get filteredGroups(): EstimateGroup[] {
    return this.groups.filter(g => matches(this.search, [g.name]) ||
      g.jobs.some(j => matches(this.search, [j.jobid, j.address])));
  }

  get pageGroups(): EstimateGroup[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredGroups.slice(start, start + this.pageSize);
  }

  get pages(): number {
    return Math.max(1, Math.ceil(this.filteredGroups.length / this.pageSize));
  }

  get pageNumbers(): number[] {
    return pageNumbers(this.page, this.pages);
  }

  goTo(page: number) {
    if (page >= 1 && page <= this.pages) {
      this.page = page;
    }
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 1;
  }

  onSearch() {
    this.page = 1;
  }

  toggle(group: EstimateGroup) {
    this.collapsed[group.key] = !this.collapsed[group.key];
  }

  get filteredJobCount(): number {
    return this.filteredGroups.reduce((n, g) => n + g.jobs.length, 0);
  }

  plural(n: number, one: string, many: string): string {
    return n + ' ' + (n === 1 ? one : many);
  }

  exportCsv() {
    const rows: any[][] = [['Inspector', 'Report #', 'Address', 'Est. Hours', 'Actual Hours', 'Hours Used %', 'Est. Materials', 'Actual Materials', 'Materials Used %']];
    this.filteredGroups.forEach(g => {
      g.jobs.forEach(j => rows.push([g.name, j.jobid, j.address, j.estimated_hours, j.actual_hours, j.perc_hours,
        j.estimated_materials, j.actual_materials, j.perc_mat]));
      rows.push([g.name + ' totals', '', '', g.totals.estimated_hours_total, g.totals.actual_hours_total, g.totals.perc_hours_total,
        g.totals.estimated_mat_total, g.totals.actual_mat_total, g.totals.perc_mat_total]);
    });
    downloadCsv('estimated-vs-actual.csv', rows);
  }
}
